import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Modal, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import ApiConfig from "../../api/ApiConfig";
import {
  simplePostCall,
  simplePUTCall,
  simpleDeleteCall,
  simpleGetCall,
} from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import { PageHead, BoolBadge } from "./ffUi";
import { ConfirmDialog, inRange, isPositiveNumber } from "./ffHelpers";
import "./FleetFinance.scss";

// FF-11: the base currency must always have an exchange rate of exactly 1
const BASE_CURRENCY_CODE = "QAR";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

// NUMERIC fields arrive as strings ("10.000") — coerce before display.
const num = (n) => (n || n === 0 ? Number(n) : "--");

const emptyForm = {
  currency_rate_id: null,
  currency_id: "",
  corporate_tax_rate: "",
  tax_depreciation_rate: "",
  exchange_rate: "",
  is_active: true,
};

const CurrencyRate = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false); // false = add, true = update
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null); // U-09

  useEffect(() => {
    getCurrencies();
    getRates();
  }, []);

  const getCurrencies = () => {
    simpleGetCall(ApiConfig.CURRNCY_LIST)
      .then((res) => {
        if (res?.result || res?.success) setCurrencies(res?.data || []);
      })
      .catch((err) => console.log("currency list err", err));
  };

  const getRates = () => {
    setLoading(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_CURRENCY_RATE_LIST, JSON.stringify({}))
      .then((data) => {
        setRows(data?.success || data?.result ? data?.data || [] : []);
      })
      .catch((err) => {
        console.log("currency-rate list err", err);
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  // Currencies that don't yet have a rate row — enforces "one row per currency".
  const availableCurrencies = currencies.filter(
    (c) => !rows.some((r) => Number(r.currency_id) === Number(c.currency_id))
  );

  const openAdd = () => {
    setEditing(false);
    setForm(emptyForm);
    setShow(true);
  };

  const openEdit = (row) => {
    setEditing(true);
    setForm({
      currency_rate_id: row.currency_rate_id,
      currency_id: row.currency_id,
      corporate_tax_rate: row.corporate_tax_rate ?? "",
      tax_depreciation_rate: row.tax_depreciation_rate ?? "",
      exchange_rate: row.exchange_rate ?? "",
      is_active: row.is_active !== false,
    });
    setShow(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!editing && !form.currency_id) {
      notifyError(t("Please select a currency"));
      return;
    }
    // FF-11: validate before save
    const currency = currencies.find(
      (x) => Number(x.currency_id) === Number(form.currency_id)
    );
    const code = String(currency?.currency_code || "").trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) {
      notifyError(t("Currency code must be 3 letters"));
      return;
    }
    if (!isPositiveNumber(form.exchange_rate)) {
      notifyError(t("Exchange rate must be greater than 0"));
      return;
    }
    if (form.corporate_tax_rate !== "" && !inRange(form.corporate_tax_rate, 0, 100)) {
      notifyError(t("Corporate tax rate must be between 0 and 100"));
      return;
    }
    if (
      form.tax_depreciation_rate !== "" &&
      !inRange(form.tax_depreciation_rate, 0, 100)
    ) {
      notifyError(t("Tax depreciation rate must be between 0 and 100"));
      return;
    }
    if (code === BASE_CURRENCY_CODE && Number(form.exchange_rate) !== 1) {
      notifyError(t("Base currency rate must be 1"));
      return;
    }
    setSaving(true);

    const done = (data, okMsg) => {
      if (data?.success || data?.result) {
        notifySuccess(data?.message || okMsg);
        setShow(false);
        getRates();
      } else {
        notifyError(data?.message || t("Something went wrong"));
      }
    };

    if (editing) {
      const body = JSON.stringify({
        currency_rate_id: form.currency_rate_id,
        corporate_tax_rate: Number(form.corporate_tax_rate) || 0,
        tax_depreciation_rate: Number(form.tax_depreciation_rate) || 0,
        exchange_rate: Number(form.exchange_rate) || 0,
        is_active: form.is_active,
      });
      simplePUTCall(ApiConfig.FLEET_FINANCE_CURRENCY_RATE_UPDATE, body)
        .then((data) => done(data, "Currency rate updated"))
        .catch((err) => {
          console.log("currency-rate update err", err);
          notifyError(t("Something went wrong"));
        })
        .finally(() => setSaving(false));
    } else {
      const body = JSON.stringify({
        currency_id: Number(form.currency_id),
        corporate_tax_rate: Number(form.corporate_tax_rate) || 0,
        tax_depreciation_rate: Number(form.tax_depreciation_rate) || 0,
        exchange_rate: Number(form.exchange_rate) || 0,
        is_active: form.is_active,
      });
      simplePostCall(ApiConfig.FLEET_FINANCE_CURRENCY_RATE_ADD, body)
        .then((data) => done(data, "Currency rate added"))
        .catch((err) => {
          console.log("currency-rate add err", err);
          notifyError(t("Something went wrong"));
        })
        .finally(() => setSaving(false));
    }
  };

  // U-09: confirm with ConfirmDialog instead of window.confirm
  const remove = (row) => setDeleteRow(row);

  const confirmRemove = () => {
    const row = deleteRow;
    setDeleteRow(null);
    if (!row) return;
    setDeletingId(row.currency_rate_id);
    simpleDeleteCall(
      ApiConfig.FLEET_FINANCE_CURRENCY_RATE_DELETE + row.currency_rate_id,
      JSON.stringify({})
    )
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Currency rate deleted");
          getRates();
        } else {
          notifyError(data?.message || t("Unable to delete"));
        }
      })
      .catch((err) => {
        console.log("currency-rate delete err", err);
        notifyError(t("Something went wrong"));
      })
      .finally(() => setDeletingId(null));
  };

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_Currency_Rate">
        <div className="main-master-wrapper">
          <PageHead
            title={t("Currency Rates")}
            subtitle={t(
              "Global tax & exchange rates per currency (shared across all tenants)"
            )}
          >
            <button
              className="cx-btn-2"
              onClick={openAdd}
              disabled={availableCurrencies.length === 0}
              title={
                availableCurrencies.length === 0
                  ? t("All currencies already have a rate")
                  : ""
              }
            >
              + {t("Add Rate")}
            </button>
          </PageHead>

          {loading ? (
            <Loader />
          ) : (
            <div id="scroll_insideThe_Padding_tabel">
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Currency")}</th>
                      <th>{t("Corporate Tax %")}</th>
                      <th>{t("Tax Depreciation %")}</th>
                      <th>{t("Exchange Rate")}</th>
                      <th>{t("Active")}</th>
                      <th>{t("Action")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {rows && rows.length > 0 ? (
                      rows.map((r) => (
                        <tr key={r.currency_rate_id}>
                          <td>
                            {[r.currency_code, r.currency_symbol && `(${r.currency_symbol})`]
                              .filter(Boolean)
                              .join(" ")}
                            {r.currency_name ? (
                              <small style={{ display: "block", opacity: 0.6 }}>
                                {r.currency_name}
                              </small>
                            ) : null}
                          </td>
                          <td>{num(r.corporate_tax_rate)}</td>
                          <td>{num(r.tax_depreciation_rate)}</td>
                          <td>{num(r.exchange_rate)}</td>
                          <td>
                            <BoolBadge value={r.is_active} />
                          </td>
                          <td>
                            <div className="d-flex" style={{ gap: 8 }}>
                              <button
                                className="cx-btn-1"
                                onClick={() => openEdit(r)}
                              >
                                {t("Edit")}
                              </button>
                              <button
                                className="cx-btn-1"
                                onClick={() => remove(r)}
                                disabled={deletingId === r.currency_rate_id}
                              >
                                {deletingId === r.currency_rate_id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  t("Delete")
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit modal */}
      <Modal show={show} onHide={() => setShow(false)} centered className="common-model">
        <Form onSubmit={submit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editing ? t("Edit Currency Rate") : t("Add Currency Rate")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-12 form_input_main">
                <Form.Label className="common-labels">
                  {t("Currency")} <span>&#42;</span>
                </Form.Label>
                {editing ? (
                  <Form.Control
                    type="text"
                    readOnly
                    disabled
                    value={
                      (() => {
                        const c = currencies.find(
                          (x) => Number(x.currency_id) === Number(form.currency_id)
                        );
                        return c
                          ? `${c.currency_code || ""} ${c.currency_name || ""}`.trim()
                          : form.currency_id;
                      })()
                    }
                  />
                ) : (
                  <Form.Select
                    name="currency_id"
                    value={form.currency_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t("Select Currency")}</option>
                    {availableCurrencies.map((c) => (
                      <option key={c.currency_id} value={c.currency_id}>
                        {[c.currency_code, c.currency_name, c.currency_symbol && `(${c.currency_symbol})`]
                          .filter(Boolean)
                          .join(" ")}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Corporate Tax Rate")} (%)
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  name="corporate_tax_rate"
                  value={form.corporate_tax_rate}
                  onChange={handleChange}
                  placeholder="10"
                  min="0"
                  max="100"
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Tax Depreciation Rate")} (%)
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  name="tax_depreciation_rate"
                  value={form.tax_depreciation_rate}
                  onChange={handleChange}
                  placeholder="20"
                  min="0"
                  max="100"
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Exchange Rate")} <span>&#42;</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.000001"
                  min="0"
                  required
                  name="exchange_rate"
                  value={form.exchange_rate}
                  onChange={handleChange}
                  placeholder="1"
                />
              </div>
              <div className="col-md-6 form_input_main d-flex align-items-center" style={{ marginTop: 28 }}>
                <Form.Check
                  type="checkbox"
                  id="currency-rate-active"
                  name="is_active"
                  label={t("Active")}
                  checked={form.is_active}
                  onChange={handleChange}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="pop-up-modal-footer">
            <div className="btn-wrapper">
              <button type="button" className="cx-btn-1" onClick={() => setShow(false)}>
                {t("Cancel")}
              </button>
              <button type="submit" className="cx-btn-2" disabled={saving}>
                {saving ? <Spinner animation="border" size="sm" /> : t("Save")}
              </button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* U-09: delete confirmation */}
      <ConfirmDialog
        show={!!deleteRow}
        title={t("Delete Currency Rate")}
        body={`${t("Delete this currency rate?")}${
          deleteRow?.currency_code ? ` (${deleteRow.currency_code})` : ""
        }`}
        confirmText={t("Delete")}
        onConfirm={confirmRemove}
        onCancel={() => setDeleteRow(null)}
        busy={!!deletingId}
      />
    </motion.div>
  );
};

export default CurrencyRate;
